import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type {
    SiteChatAttachment,
    SiteChatAuthor,
    SiteChatConfig,
    SiteChatMessage,
    SiteChatModel,
    SiteChatTheme,
    SiteChatUpload,
    SiteChatVoice,
} from '../types';
import { defaultSiteChatConfig } from '../phrases';
import { linkHref, linkLabel } from '../links';
import type { SiteChatLinkType } from '../types';
import { intlLocale, siteChatUi, type SiteChatUi } from '../ui';
import { Virtuoso, type VirtuosoHandle } from 'react-virtuoso';
import { EmojiButton, VoiceDraft, VoiceRecorder } from './media';
import { Attachments, Lightbox, UploadItem, ViewerContext, isBannerFile, type ViewerItem } from './files';
import { MarkdownText } from './markdown';
import { PhoneField } from './phone';

const DEFAULT_COLORS = defaultSiteChatConfig('en').colors;

/** `system` follows the OS, live: switching the OS theme repaints an open widget. */
export function useDarkTheme(theme: SiteChatTheme | undefined): boolean {
    const query = typeof window !== 'undefined' ? window.matchMedia?.('(prefers-color-scheme: dark)') : undefined;
    const [systemDark, setSystemDark] = useState(() => !!query?.matches);
    useEffect(() => {
        if (theme !== 'system' || !query)
            return;
        const update = () => setSystemDark(query.matches);
        update();
        query.addEventListener?.('change', update);
        return () => query.removeEventListener?.('change', update);
    }, [theme, query]);
    return theme === 'dark' || (theme === 'system' && systemDark);
}

const AVATAR_COLORS = ['#c41d7f|#ffd6e7', '#0958d9|#bae0ff', '#389e0d|#d9f7be', '#d46b08|#ffe7ba'];

const PATH = {
    chat: 'M4 4h16a1 1 0 0 1 1 1v11a1 1 0 0 1-1 1h-8.5L7 20.5V17H4a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1z',
    x: 'M6 6l12 12M18 6L6 18',
    back: 'M15 5l-7 7 7 7',
    chev: 'M9 5l7 7-7 7',
    send: 'M12 19V5M5.5 11.5L12 5l6.5 6.5',
    down: 'M12 5v14M5.5 12.5L12 19l6.5-6.5',
    clip: 'M20.5 11.5l-8.2 8.2a5 5 0 0 1-7-7l8.5-8.5a3.3 3.3 0 0 1 4.7 4.7L10 17.4a1.7 1.7 0 0 1-2.4-2.4l7.7-7.7',
    file: 'M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8zM14 3v5h5',
    retry: 'M4 12a8 8 0 0 1 14-5.3M20 4v5h-5M20 12a8 8 0 0 1-14 5.3M4 20v-5h5',
    warn: 'M12 8v5M12 16.5v.5M10.3 3.9L2.4 18a2 2 0 0 0 1.7 3h15.8a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z',
    check: 'M6 12.5l4 4 8-9',
};

/** Line icons of «Other ways to reach us», in the widget's own stroke style; the brand shows in the colour. */
const LINK_ICONS: Record<SiteChatLinkType, string> = {
    telegram: 'M21 4.5L3 11.3l5.6 2 2 6.2 3.2-3.9 4.9 3.6L21 4.5zM8.6 13.3L17 8l-6.4 7.2',
    whatsapp: 'M4 20l1.3-3.9A8.5 8.5 0 1 1 8.2 19zM9.2 8.4c.1 2.7 3.3 6 6.2 6.3l1.1-1.3-2-1.1-.9.8c-1-.5-2.1-1.6-2.5-2.6l.8-.9-1-2z',
    instagram: 'M7.5 3h9A4.5 4.5 0 0 1 21 7.5v9a4.5 4.5 0 0 1-4.5 4.5h-9A4.5 4.5 0 0 1 3 16.5v-9A4.5 4.5 0 0 1 7.5 3zM12 8.3a3.7 3.7 0 1 0 0 7.4 3.7 3.7 0 0 0 0-7.4zM17.3 6.7v.1',
    facebook: 'M14.5 8.5H17V4.8h-2.8c-2.4 0-3.9 1.6-3.9 4v2.1H7.8v3.7h2.5V21h3.8v-6.4h2.6l.5-3.7h-3.1V9.3c0-.5.3-.8.4-.8z',
    vk: 'M3 7.5h3.4c.6 3 2.1 5.3 3.4 5.8V7.5h3.1v3.4c1.4-.2 2.8-1.9 3.4-3.4h3c-.5 2.3-2.4 4.3-3.9 5.1 1.5.7 3.4 2.4 4.3 4.6h-3.4c-.8-1.6-2.2-2.9-3.4-3.1v3.1h-.5C7.5 17.2 3.6 13.4 3 7.5z',
    viber: 'M12 3c5 0 8.5 2 8.5 7.3S17 17.8 12 17.8c-.7 0-1.4 0-2-.1L7 20.5v-3.4c-2.4-1.3-3.5-3.5-3.5-6.8C3.5 5 7 3 12 3zM9.6 7.8c.3 2.5 2.6 4.8 5.1 5.1l.8-1-1.4-.9-.7.6c-.8-.4-1.5-1.1-1.9-1.9l.6-.7-.9-1.4z',
    email: 'M3.5 6h17v12h-17zM3.5 6.5l8.5 6 8.5-6',
    phone: 'M5.2 4h3.6l1.8 4.6-2.3 1.4a11 11 0 0 0 5.7 5.7l1.4-2.3 4.6 1.8v3.6a2 2 0 0 1-2.2 2A16.5 16.5 0 0 1 3.2 6.2 2 2 0 0 1 5.2 4z',
    url: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18zM3.5 9h17M3.5 15h17M12 3c2.4 2.4 3.6 5.4 3.6 9s-1.2 6.6-3.6 9c-2.4-2.4-3.6-5.4-3.6-9S9.6 5.4 12 3',
};

const LINK_COLORS: Partial<Record<SiteChatLinkType, string>> = {
    telegram: '#229ed9',
    whatsapp: '#25d366',
    instagram: '#e4405f',
    facebook: '#1877f2',
    vk: '#0077ff',
    viber: '#7360f2',
};

/**
 * Keeps an element in the DOM for `ms` after `on` turns false, so it can play its way out (`leaving`) instead of
 * vanishing. Coming back while it leaves just keeps it.
 */
function usePresence(on: boolean, ms: number): { mounted: boolean; leaving: boolean } {
    const [mounted, setMounted] = useState(on);
    useEffect(() => {
        if (on) {
            setMounted(true);
            return;
        }
        const timer = window.setTimeout(() => setMounted(false), ms);
        return () => window.clearTimeout(timer);
    }, [on, ms]);
    return { mounted: on || mounted, leaving: !on && mounted };
}

function Icon({ d, size = 20, fill = false }: { d: string; size?: number; fill?: boolean }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill={fill ? 'currentColor' : 'none'} stroke={fill ? 'none' : 'currentColor'}
            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d={d} />
        </svg>
    );
}

function Avatar({ name, photo, size }: { name?: string | null; photo?: string | null; size: number }) {
    const label = (name ?? '').trim();
    // Every character counts: «Анна», «Иван», «Мария» share nothing mod 4 in their first letters.
    const index = [...label].reduce((sum, char) => sum + char.charCodeAt(0), 0) % AVATAR_COLORS.length;
    const [fg, bg] = AVATAR_COLORS[index].split('|');
    return (
        <div
            className="axl-sc-av"
            style={{
                width: size,
                height: size,
                background: photo ? `url("${photo}") center/cover` : bg,
                color: fg,
                fontSize: Math.round(size * 0.4),
            }}
        >
            {photo ? null : label ? label.slice(0, 1).toUpperCase() : <Icon d={PATH.chat} size={Math.round(size * 0.5)} fill />}
        </div>
    );
}

/**
 * The school's people the visitor sees: whoever took their conversation, else the channel's team; at most three.
 * «Show team» off: nobody.
 */
export function visiblePeople(model: Pick<SiteChatModel, 'config' | 'team' | 'operators'>): SiteChatAuthor[] {
    if (!model.config.showTeam)
        return [];
    const named = (people?: SiteChatAuthor[]) => (people ?? []).filter(person => person.name?.trim());
    const operators = named(model.operators);
    return (operators.length ? operators : named(model.team)).slice(0, 3);
}

export type WidgetViewProps = {
    model: SiteChatModel;
    /** `page`: fixed to the visitor's viewport. `stage`: absolute inside a preview frame. */
    frame: 'page' | 'stage';
    phone: boolean;
    /** Preview only: draws the launcher in its hover state. */
    hover?: boolean;
    /** Live phone only: the visual viewport height, so the composer stays above the keyboard. */
    viewportHeight?: number | null;
};

export function WidgetView({ model, frame, phone, hover = false, viewportHeight }: WidgetViewProps) {
    const { config } = model;
    const ui = siteChatUi(config.lang);
    const dark = useDarkTheme(config.theme);
    const school = model.mode === 'school';
    const side = config.position === 'left' ? 'left' : 'right';
    const offset = phone ? 16 : config.offsetX;
    const bottom = phone ? 16 : config.offsetY;
    // The light default operator bubble would glare on a dark panel; a colour the owner picked stays theirs.
    const operatorBubble = dark && config.colors.operatorBubble === DEFAULT_COLORS.operatorBubble ? '#303030' : config.colors.operatorBubble;
    const operatorText = dark && config.colors.operatorText === DEFAULT_COLORS.operatorText ? 'rgba(255,255,255,.88)' : config.colors.operatorText;
    const style = {
        ['--sc-btn' as string]: config.colors.button,
        ['--sc-head' as string]: config.colors.header,
        ['--sc-guest' as string]: config.colors.guestBubble,
        ['--sc-guest-text' as string]: config.colors.guestText,
        ['--sc-op' as string]: operatorBubble,
        ['--sc-op-text' as string]: operatorText,
    } as React.CSSProperties;
    const buttonStyle: React.CSSProperties = side === 'left' ? { left: offset, bottom } : { right: offset, bottom };
    const panelStyle: React.CSSProperties = phone
        ? (viewportHeight ? { height: viewportHeight, bottom: 'auto' } : {})
        : school
            // Under the host's header, where its chat button is — the place the old floating chat opened.
            ? { right: 16, top: 72, bottom: 'auto', width: config.width, height: 'min(640px, calc(100% - 88px))' }
            : {
                [side]: offset,
                bottom: bottom + 76,
                width: config.width,
                maxHeight: `calc(100% - ${bottom + 76 + 16}px)`,
            };
    const showButton = !school && !(model.open && phone);
    // The picture viewer covers the whole layer (the page, or the preview frame) above the panel.
    const [viewer, setViewer] = useState<{ items: ViewerItem[]; index: number } | null>(null);
    const viewerApi = React.useMemo(() => ({ open: (items: ViewerItem[], index: number) => setViewer({ items, index }) }), []);
    // The panel and the hint play their way out; the hint keeps its last text while it fades.
    const panel = usePresence(model.open, 200);
    const hintOn = !school && !model.open && !!model.hint;
    const hint = usePresence(hintOn, 180);
    const lastHint = useRef(model.hint);
    if (model.hint)
        lastHint.current = model.hint;

    // Nothing until the channel's config is in: no button at the default corner jumping to the configured one.
    if (frame === 'page' && model.booted === false)
        return null;

    return (
        <ViewerContext.Provider value={viewerApi}>
        <div className={`axl-sc axl-sc-layer ${frame === 'page' ? 'fixed' : ''} ${phone ? 'phone' : ''} ${school ? 'school' : ''}`}
            data-theme={dark ? 'dark' : 'light'} style={model.capturing ? { ...style, visibility: 'hidden' } : style}>
            {viewer && model.open ? <Lightbox items={viewer.items} index={viewer.index} ui={ui} onClose={() => setViewer(null)} /> : null}
            {panel.mounted ? (
                <div className={`axl-sc-pan ${panel.leaving ? 'out' : ''}`} data-side={side} style={panelStyle} role="dialog"
                    aria-label={model.config.title} aria-hidden={panel.leaving || undefined}>
                    <Panel model={model} ui={ui} />
                </div>
            ) : null}
            {hint.mounted ? (
                <div className={`axl-sc-hint ${hint.leaving ? 'out' : ''}`} data-side={side}
                    style={side === 'left' ? { left: offset + 68, bottom: bottom + 8 } : { right: offset + 68, bottom: bottom + 8 }}>
                    <button type="button" className="axl-sc-hint-text" onClick={model.actions.openHint}>{model.hint ?? lastHint.current}</button>
                    <button className="x" type="button" aria-label={ui.close} onClick={model.actions.dismissHint}><Icon d={PATH.x} size={14} /></button>
                </div>
            ) : null}
            {showButton ? (
                <button
                    type="button"
                    className={`axl-sc-btn ${hover ? 'hov' : ''} ${model.open ? 'open' : ''}`}
                    style={buttonStyle}
                    aria-label={model.open ? ui.close : ui.open}
                    onClick={model.actions.toggle}
                    data-sc="launcher"
                >
                    {model.loading
                        ? <span className="axl-sc-spin" />
                        : <>
                            {/* Both icons stay in the button and cross-fade with a turn, instead of swapping in one frame. */}
                            <span className="axl-sc-bi chat">
                                {config.logo
                                    ? <span className="axl-sc-logo" style={{ backgroundImage: `url("${config.logo}")` }} />
                                    : <Icon d={PATH.chat} size={26} fill />}
                            </span>
                            <span className="axl-sc-bi x"><Icon d={PATH.x} size={24} /></span>
                        </>}
                    {!model.open && model.unread > 0
                        // Keyed by the count: every new unread message pops the badge again.
                        ? <span key={model.unread} className="axl-sc-badge">{model.unread > 9 ? '9+' : model.unread}</span>
                        : null}
                </button>
            ) : null}
        </div>
        </ViewerContext.Provider>
    );
}

type PanelScreen = 'home' | 'thread' | 'error';

function Panel({ model, ui }: { model: SiteChatModel; ui: SiteChatUi }) {
    // The home card («Send a message») leads to the thread to write: the composer takes the focus then, and only then —
    // a panel opening straight into an existing thread or a trigger phrase keeps the focus where it was.
    const previousView = useRef(model.view);
    const fromHome = previousView.current === 'home' && model.view === 'thread';
    useEffect(() => {
        previousView.current = model.view;
    });
    // A screen that replaces another slides in from the side it comes from: forward into the thread, back home.
    // The first screen of an opening panel does not slide — the panel itself grows in. Kept per screen, so a
    // re-render mid-slide does not cut the animation short.
    const screen: PanelScreen = model.connectionError ? 'error' : model.view;
    const slide = useRef<{ screen: PanelScreen; dir?: 'fwd' | 'back' }>({ screen });
    if (slide.current.screen !== screen)
        slide.current = { screen, dir: screen === 'home' ? 'back' : 'fwd' };

    let content: React.ReactNode;
    if (screen === 'error')
        content = (
            <>
                <ThreadHead model={model} ui={ui} />
                <div className="axl-sc-nc" data-sc="no-connection">
                    <div>{model.config.noConnection}</div>
                    <button className="axl-sc-retry" type="button" onClick={model.actions.retry}>
                        <Icon d={PATH.retry} size={16} />{model.config.retry}
                    </button>
                </div>
            </>
        );
    else if (screen === 'thread')
        content = <Thread model={model} ui={ui} autoFocus={fromHome} />;
    else
        content = <Home model={model} ui={ui} />;
    return (
        <div key={screen} className="axl-sc-view" data-dir={slide.current.dir}>
            {content}
            {model.mode === 'site' ? <PoweredBy ui={ui} /> : null}
        </div>
    );
}

/** The AI chat product page; the same UTM shape as the booking widget's and the quiz's ribbons. */
const POWERED_BY_URL = 'https://axl.tech/ai-chat?utm_source=site_chat&utm_medium=powered_by&utm_campaign=chat';

/**
 * «Powered by AXL» at the foot of the panel, under every screen, on websites only (`mode === 'site'`, which the
 * admin preview shares, so the preview shows what a visitor sees). Owner 2026-10-19: the chat widget keeps it on
 * every plan, like bookings and quizzes. The signed-in hosts (School membership area, the admin's support chat)
 * have no footer. `utm_content` = the host page's domain — which account's site referred the visitor.
 */
function PoweredBy({ ui }: { ui: SiteChatUi }) {
    const host = typeof window !== 'undefined' && window.location?.hostname
        ? `&utm_content=${encodeURIComponent(window.location.hostname)}`
        : '';
    return (
        <a className="axl-sc-pby" href={POWERED_BY_URL + host} target="_blank" rel="noopener noreferrer" data-sc="powered-by">
            {ui.poweredBy} <b>AXL</b>
        </a>
    );
}

function StatusLine({ model, ui, compact }: { model: SiteChatModel; ui: SiteChatUi; compact?: boolean }) {
    const { config, online } = model;
    const text = config.showReplyTime
        ? (online ? config.replyOnline : config.replyOffline)
        : compact ? (online ? ui.online : ui.offline) : null;
    if (!text)
        return null;
    return (
        <div className={compact ? 'axl-sc-ths' : 'axl-sc-st'}>
            <i className={`axl-sc-dot ${online ? '' : 'off'}`} />
            <span>{text}</span>
        </div>
    );
}

function Home({ model, ui }: { model: SiteChatModel; ui: SiteChatUi }) {
    const { config } = model;
    const people = visiblePeople(model);
    // The field shares its text with the thread's composer: what was typed here is there after the switch.
    const text = model.composer.text;
    const typed = !!text.trim();
    // Empty: the arrow just opens the thread. With text: it goes out the way the thread's send button sends it
    // (held under a required form, the form card, limits) and the thread opens on it.
    const start = () => {
        model.actions.openThread();
        if (typed)
            model.actions.send();
    };
    const links = config.links
        .map(link => ({ link, href: linkHref(link) }))
        .filter((item): item is { link: typeof item.link; href: string } => !!item.href);
    // A visitor who already wrote gets back to that conversation from here, not a field that looks like a new one.
    const last = [...model.messages].reverse().find(message => !message.local);
    return (
        <>
            <div className="axl-sc-hh" data-sc="home">
                <button className="x" type="button" aria-label={ui.close} onClick={model.actions.close}><Icon d={PATH.x} /></button>
                <div className="axl-sc-ht">{config.title}</div>
                <div className="axl-sc-hs">{model.online ? config.subtitleOnline : config.subtitleOffline}</div>
            </div>
            <div className="axl-sc-hb">
                {people.length > 0 ? (
                    <div>
                        <div className="axl-sc-avs">
                            {people.map((person, index) => <Avatar key={index} name={person.name} photo={person.avatarUrl} size={40} />)}
                        </div>
                        <div className="axl-sc-names">{joinNames(people.map(person => (person.name ?? '').trim()), config.lang)}</div>
                    </div>
                ) : null}
                <StatusLine model={model} ui={ui} />
                {last ? <ConversationCard model={model} ui={ui} last={last} /> : (
                    <div className="axl-sc-hin" data-sc="home-input">
                        <input
                            className="axl-sc-hinp"
                            type="text"
                            value={text}
                            placeholder={config.cardText}
                            aria-label={config.cardText}
                            maxLength={config.maxLength}
                            enterKeyHint="send"
                            onChange={e => model.actions.setText(e.target.value)}
                            onKeyDown={e => {
                                if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
                                    e.preventDefault();
                                    start();
                                }
                            }}
                        />
                        <button type="button" className={`axl-sc-hsnd ${typed ? 'on' : ''}`} aria-label={typed ? ui.send : ui.open} onClick={start} data-sc="home-send">
                            <span className="axl-sc-bi chat"><Icon d={PATH.chev} size={18} /></span>
                            <span className="axl-sc-bi x"><Icon d={PATH.send} size={18} /></span>
                        </button>
                    </div>
                )}
                {links.length > 0 ? (
                    <div className="axl-sc-links" data-sc="links">
                        <div className="axl-sc-lt">{config.linksTitle}</div>
                        {links.map(({ link, href }, index) => (
                            <a key={index} className="axl-sc-link" href={href} rel="noopener noreferrer"
                                target={/^https?:/i.test(href) ? '_blank' : undefined} data-sc={`link-${link.type}`}>
                                <span className="axl-sc-li" style={LINK_COLORS[link.type] ? { color: LINK_COLORS[link.type] } : undefined}>
                                    <Icon d={LINK_ICONS[link.type]} size={20} />
                                </span>
                                <span className="axl-sc-ll">{linkLabel(link)}</span>
                                <Icon d={PATH.chev} size={16} />
                            </a>
                        ))}
                    </div>
                ) : null}
                <Policy config={config} ui={ui} />
            </div>
        </>
    );
}

/**
 * The home screen's way back into the conversation: who wrote last and what, when, and how many replies are unread.
 * The whole card opens the thread.
 */
function ConversationCard({ model, ui, last }: { model: SiteChatModel; ui: SiteChatUi; last: SiteChatMessage }) {
    const locale = intlLocale(model.config.lang);
    const operator = last.fromVisitor ? null : last.author ?? visiblePeople(model)[0] ?? null;
    const person = operator ?? visiblePeople(model)[0] ?? null;
    const attachment = last.attachments?.[0];
    const text = last.text?.trim()
        || (attachment ? (attachment.voice ? ui.voiceMessage : attachment.name) : '');
    const who = last.fromVisitor ? ui.you : operator?.name?.trim() || model.channelName?.trim() || ui.support;
    const when = last.createdDate
        ? (dayKey(last.createdDate) === dayKey(new Date().toISOString()) ? timeLabel(last.createdDate, locale) : dayLabel(last.createdDate, locale, ui))
        : '';
    return (
        <button type="button" className="axl-sc-conv" data-sc="home-conversation" onClick={model.actions.openThread}>
            <Avatar name={person?.name ?? model.channelName} photo={person?.avatarUrl ?? model.config.logo} size={40} />
            <span className="axl-sc-cvb">
                <span className="axl-sc-cvt">
                    <b>{ui.conversation}</b>
                    {when ? <span className="axl-sc-cvd">{when}</span> : null}
                </span>
                <span className="axl-sc-cvm">
                    <span className="axl-sc-cvx">{who}: {text}</span>
                    {model.unread > 0
                        ? <span key={model.unread} className="axl-sc-cvn">{model.unread > 9 ? '9+' : model.unread}</span>
                        : null}
                </span>
            </span>
            <Icon d={PATH.chev} size={18} />
        </button>
    );
}

function Policy({ config, ui }: { config: SiteChatConfig; ui: SiteChatUi }) {
    if (!config.policyUrl?.trim())
        return null;
    return <a className="axl-sc-pol" href={safeUrl(config.policyUrl)} target="_blank" rel="noopener noreferrer">{ui.policy}</a>;
}

type FormPhase = 'form' | 'thanks' | 'fold';

/**
 * Where the lead form lives in the thread, and how it leaves. Filled in: «thank you» fades in INSIDE the card (the
 * card keeps its height, so nothing above it moves), then the card folds away. Skipped: it folds away at once. The
 * fold animates the height, so the thread glides instead of jumping.
 */
function LeadFormSlot({ model, ui, autoFocus }: { model: SiteChatModel; ui: SiteChatUi; autoFocus?: boolean }) {
    const { shown, submitted } = model.form;
    const [phase, setPhase] = useState<FormPhase | null>(shown ? 'form' : null);
    // On its way out the card shows the form exactly as it last was: once the lead is in, the model calls the form
    // optional (a «Skip» would pop up and the card would grow under the «thank you»).
    const lastForm = useRef(model.form);
    if (shown)
        lastForm.current = model.form;
    // The card just went away in the model: it stays on screen, as thanks or as the form, until it has folded.
    const view: FormPhase | null = shown ? 'form' : phase === 'form' ? (submitted ? 'thanks' : 'fold') : phase;
    useEffect(() => {
        if (view !== phase)
            setPhase(view);
    }, [view, phase]);
    useEffect(() => {
        if (phase !== 'thanks' && phase !== 'fold')
            return;
        const timer = window.setTimeout(() => setPhase(phase === 'thanks' ? 'fold' : null), phase === 'thanks' ? 1400 : 360);
        return () => window.clearTimeout(timer);
    }, [phase]);
    if (!view)
        return null;
    const thanks = view === 'thanks' || (view === 'fold' && submitted);
    return (
        <div className={`axl-sc-fold ${view === 'fold' ? 'closed' : ''}`}>
            <div className="axl-sc-foldi">
                <div className="axl-sc-item">
                    <div className={`axl-sc-fstack ${thanks ? 'done' : ''}`}>
                        <div className="axl-sc-fbase" aria-hidden={thanks || undefined}>
                            <LeadFormCard model={shown ? model : { ...model, form: lastForm.current }} ui={ui} autoFocus={autoFocus && model.form.required} />
                        </div>
                        {thanks ? (
                            <div className="axl-sc-fdone" data-sc="form-thanks" role="status">
                                <span className="axl-sc-ok"><Icon d={PATH.check} size={24} /></span>
                                <span>{ui.formThanks}</span>
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>
        </div>
    );
}

/**
 * The lead form as a card in the thread, under the visitor's first message. Required: the waiting message goes out
 * once it is filled in, and the composer waits. Optional: the visitor may skip it and go on.
 */
function LeadFormCard({ model, ui, autoFocus = false }: { model: SiteChatModel; ui: SiteChatUi; autoFocus?: boolean }) {
    const { config, form } = model;
    const sending = form.state === 'sending';
    // Sent from the home screen while the form is required: the composer is locked, so the typing goes on here.
    const firstRef = useRef<HTMLInputElement | null>(null);
    useEffect(() => {
        if (autoFocus)
            firstRef.current?.focus({ preventScroll: true });
    }, []);
    const firstField = config.fields.findIndex(field => field.enabled);
    return (
        // Not a <form>: the admin preview renders this inside the channel's own antd form, where a nested form is
        // invalid markup and its submit would bubble into saving the channel. Enter submits by hand.
        <div
            className="axl-sc-fcard"
            role="form"
            data-sc="form"
            aria-label={config.formPhrases.title}
            onKeyDown={e => {
                if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT' && !e.nativeEvent.isComposing) {
                    e.preventDefault();
                    if (!sending)
                        model.actions.submitForm();
                }
            }}
        >
            <div className="axl-sc-fht">{config.formPhrases.title}</div>
            {config.fields.map((field, index) => {
                if (!field.enabled)
                    return null;
                const error = form.errors[index];
                const id = `axl-sc-f${index}`;
                if (field.type === 'phone')
                    return (
                        <div className="axl-sc-fld" key={index}>
                            <label htmlFor={id}>{field.name}{field.required ? <i>*</i> : null}</label>
                            <PhoneField
                                id={id}
                                inputRef={index === firstField ? firstRef : undefined}
                                value={form.values[index] ?? ''}
                                placeholder={field.placeholder}
                                disabled={sending}
                                error={!!error}
                                lang={config.lang}
                                ui={ui}
                                onChange={value => model.actions.setFormValue(index, value)}
                            />
                            {error ? <div className="axl-sc-em">{formError(error, config)}</div> : null}
                        </div>
                    );
                return (
                    <div className="axl-sc-fld" key={index}>
                        <label htmlFor={id}>{field.name}{field.required ? <i>*</i> : null}</label>
                        <input
                            id={id}
                            ref={index === firstField ? firstRef : undefined}
                            className={`axl-sc-in ${error ? 'err' : ''}`}
                            type={field.type === 'email' ? 'email' : 'text'}
                            inputMode={field.type === 'email' ? 'email' : 'text'}
                            autoComplete={field.type === 'email' ? 'email' : field.builtin ? 'name' : 'off'}
                            placeholder={field.placeholder}
                            value={form.values[index] ?? ''}
                            disabled={sending}
                            maxLength={500}
                            onChange={e => model.actions.setFormValue(index, e.target.value)}
                        />
                        {error ? <div className="axl-sc-em">{formError(error, config)}</div> : null}
                    </div>
                );
            })}
            <button type="button" className={`axl-sc-pb ${sending ? 'load' : ''}`} disabled={sending} onClick={model.actions.submitForm}>
                {sending ? <span className="axl-sc-spin sm" /> : null}
                {config.formPhrases.button}
            </button>
            {form.required ? null : (
                <button type="button" className="axl-sc-skip" disabled={sending} onClick={model.actions.skipForm} data-sc="form-skip">
                    {ui.skipForm}
                </button>
            )}
            {form.state === 'network' ? <div className="axl-sc-ne"><Icon d={PATH.warn} size={16} />{config.formPhrases.network}</div> : null}
            <Policy config={config} ui={ui} />
        </div>
    );
}

function formError(code: string, config: SiteChatConfig) {
    switch (code) {
        case 'email': return config.formPhrases.email;
        case 'phone': return config.formPhrases.phone;
        default: return config.formPhrases.empty;
    }
}

function ThreadHead({ model, ui }: { model: SiteChatModel; ui: SiteChatUi }) {
    const person = visiblePeople(model)[0];
    // No home screen to go back to: the membership area's single thread, or a channel that opens straight into it.
    const solo = model.mode === 'school' || model.config.skipHome;
    return (
        <div className={`axl-sc-th ${solo ? 'solo' : ''}`}>
            {solo
                ? null
                : <button className="ib" type="button" aria-label={ui.back} onClick={model.actions.back}><Icon d={PATH.back} /></button>}
            <Avatar name={person?.name ?? model.channelName} photo={person?.avatarUrl ?? model.config.logo} size={32} />
            <div className="axl-sc-thi">
                <div className="axl-sc-thn">{model.channelName?.trim() || ui.support}</div>
                <StatusLine model={model} ui={ui} compact />
            </div>
            <button className="ib" type="button" aria-label={ui.close} onClick={model.actions.close}><Icon d={PATH.x} /></button>
        </div>
    );
}

/**
 * Any number of first items the list can be prepended to: `firstItemIndex` only decreases (by the count of older
 * messages a page brings), and must stay positive.
 */
const FIRST_ITEM = 1_000_000_000;

/**
 * The thread is virtualised (react-virtuoso): only the bubbles near the viewport are in the DOM, so a thread that
 * grew to thousands of messages scrolls as lightly as a short one. One list item = one message; its day divider and
 * the operator's name are drawn inside the item, so prepending a page shifts every index by exactly the page size
 * and the list keeps its place while older messages load above.
 */
function Thread({ model, ui, autoFocus = false }: { model: SiteChatModel; ui: SiteChatUi; autoFocus?: boolean }) {
    const { config } = model;
    const listRef = useRef<VirtuosoHandle>(null);
    const scrollerRef = useRef<HTMLElement | null>(null);
    const atBottomRef = useRef(true);
    // Scrolled away from the newest message: a round button brings the visitor down, with the count of replies that
    // came meanwhile (the launcher badge shows the same count while the panel is closed).
    const [atBottom, setAtBottom] = useState(true);
    const locale = intlLocale(config.lang);

    const messages: SiteChatMessage[] = model.messages.length === 0 && config.greeting?.trim()
        ? [{ id: 'greeting', index: -1, text: config.greeting, fromVisitor: false, createdDate: '', local: true }]
        : model.messages;
    const onlyLocal = messages.every(message => message.local);
    const last = messages[messages.length - 1];

    // Older pages come in at the front: move the first index back by as many messages as arrived before the old first.
    const firstRef = useRef({ index: FIRST_ITEM, id: messages[0]?.id ?? null });
    const firstId = messages[0]?.id ?? null;
    if (firstId !== firstRef.current.id) {
        const shift = firstRef.current.id ? messages.findIndex(message => message.id === firstRef.current.id) : -1;
        if (shift > 0)
            firstRef.current.index -= shift;
        firstRef.current.id = firstId;
    }
    const firstItemIndex = firstRef.current.index;

    // Only a message that turns up while the thread is on screen slides in. What was there when it opened, older pages
    // loaded above, and bubbles the virtual list re-mounts on scroll appear as they are. A sent message keeps its key
    // (the client id) when the server's copy replaces it, so it is never animated twice. Messages still on their way
    // when the thread opens (just sent from the home screen) count as new.
    const born = useRef<Map<string, number> | null>(null);
    if (!born.current)
        born.current = new Map(messages.map(message => [itemKey(message), message.status ? performance.now() : 0]));
    const isFresh = (message: SiteChatMessage, position: number) => {
        const key = itemKey(message);
        const now = performance.now();
        let at = born.current!.get(key);
        if (at === undefined) {
            at = position >= messages.length - 3 ? now : 0;
            born.current!.set(key, at);
        }
        return at > 0 && now - at < FRESH_MS;
    };

    const toBottom = () => {
        const scroller = scrollerRef.current;
        if (scroller)
            scroller.scrollTop = scroller.scrollHeight;
    };

    // Own messages always bring the reader down; others only while the reader is already at the bottom (followOutput).
    useLayoutEffect(() => {
        if (last?.fromVisitor)
            listRef.current?.scrollToIndex({ index: 'LAST', align: 'end' });
    }, [last?.id, last?.fromVisitor]);
    // The typing row lives in the footer, outside the items: follow it by hand.
    useLayoutEffect(() => {
        if ((model.typing || model.form.shown) && atBottomRef.current)
            toBottom();
    }, [model.typing, model.form.shown]);
    // Pictures load after the first layout and push the newest bubbles below the fold: re-pin while at the bottom.
    useEffect(() => {
        const scroller = scrollerRef.current;
        if (!scroller)
            return;
        const onLoad = (e: Event) => {
            if (atBottomRef.current && (e.target as HTMLElement)?.tagName === 'IMG')
                toBottom();
        };
        scroller.addEventListener('load', onLoad, true);
        return () => scroller.removeEventListener('load', onLoad, true);
    }, []);

    const renderItem = (absoluteIndex: number, message: SiteChatMessage) => {
        const i = absoluteIndex - firstItemIndex;
        const prev = messages[i - 1];
        const next = messages[i + 1];
        const day = message.createdDate ? dayKey(message.createdDate) : '';
        const newDay = !!day && (!prev?.createdDate || dayKey(prev.createdDate) !== day);
        const divider = newDay ? <div className="axl-sc-date">{dayLabel(message.createdDate, locale, ui)}</div> : null;
        const itemClass = `axl-sc-item ${isFresh(message, i) ? 'fresh' : ''}`;
        if (message.fromVisitor)
            return (
                <div className={itemClass}>
                    {divider}
                    <GuestBubble message={message} locale={locale} ui={ui} onResend={model.actions.resend}
                        receipt={config.readReceipts ? receiptOf(message, model.readIndex ?? -1) : null} />
                </div>
            );
        const firstOfGroup = !prev || prev.fromVisitor || authorKey(prev.author) !== authorKey(message.author) || newDay;
        const lastOfGroup = !next || next.fromVisitor || authorKey(next.author) !== authorKey(message.author);
        const author = message.author ?? visiblePeople(model)[0] ?? null;
        return (
            <div className={itemClass}>
                {divider}
                {firstOfGroup && author?.name && !message.local ? <div className="axl-sc-oname">{author.name}</div> : null}
                <OperatorBubble
                    message={message}
                    author={author}
                    avatar={lastOfGroup}
                    locale={locale}
                    ui={ui}
                    onButton={button => model.actions.clickButton(message, button)}
                />
            </div>
        );
    };

    return (
        <>
            <ThreadHead model={model} ui={ui} />
            <div className="axl-sc-msgw">
            <Virtuoso<SiteChatMessage>
                ref={listRef}
                scrollerRef={element => { scrollerRef.current = element as HTMLElement | null; }}
                className={`axl-sc-msgs ${onlyLocal ? 'top' : ''}`}
                data-sc={model.messages.length ? 'thread' : 'thread-empty'}
                data={messages}
                computeItemKey={(_, message) => itemKey(message)}
                firstItemIndex={firstItemIndex}
                initialTopMostItemIndex={{ index: 'LAST', align: 'end' }}
                alignToBottom={!onlyLocal}
                followOutput={atBottom => atBottom ? 'auto' : false}
                atBottomStateChange={bottom => {
                    atBottomRef.current = bottom;
                    setAtBottom(bottom);
                    model.actions.setAtBottom(bottom);
                }}
                atBottomThreshold={80}
                increaseViewportBy={{ top: 400, bottom: 200 }}
                startReached={model.hasMore ? model.actions.loadMore : undefined}
                itemContent={renderItem}
                components={{ Header: ThreadTop, Footer: ThreadBottom }}
                context={{ model, ui, autoFocus }}
            />
            {atBottom ? null : (
                <button type="button" className="axl-sc-jump" data-sc="jump" aria-label={ui.latest} title={ui.latest}
                    onClick={() => listRef.current?.scrollToIndex({ index: 'LAST', align: 'end', behavior: 'smooth' })}>
                    <Icon d={PATH.down} size={18} />
                    {model.unread > 0
                        // Keyed by the count: every new reply pops the badge again, as on the launcher.
                        ? <span key={model.unread} className="axl-sc-badge">{model.unread > 9 ? '9+' : model.unread}</span>
                        : null}
                </button>
            )}
            </div>
            {!model.online && config.offlineBanner?.trim() ? <div className="axl-sc-off">{config.offlineBanner}</div> : null}
            <Composer model={model} ui={ui} autoFocus={autoFocus} />
        </>
    );
}

/** `autoFocus`: the thread was just opened from the home screen, where the visitor was typing. */
type ThreadContext = { model: SiteChatModel; ui: SiteChatUi; autoFocus?: boolean };

/** Above the first message: «earlier» while older pages remain (reaching the top also loads them), else the top gutter. */
function ThreadTop({ context }: { context?: ThreadContext }) {
    if (!context?.model.hasMore)
        return <div className="axl-sc-edge" />;
    return (
        <div className="axl-sc-more-row">
            <button type="button" className="axl-sc-more" onClick={context.model.actions.loadMore}>{context.ui.earlier}</button>
        </div>
    );
}

/** How long a new bubble keeps its «fresh» class: longer than its slide-in, so a re-render never cuts it short. */
const FRESH_MS = 700;

/**
 * A message's key in the list: the client id of a visitor's message (the optimistic bubble and the server's copy
 * share it, so the bubble stays the same element), else the id.
 */
function itemKey(message: SiteChatMessage) {
    return message.clientId || message.id;
}

/** Below the last message: the lead form, the typing row and the bottom gutter. */
function ThreadBottom({ context }: { context?: ThreadContext }) {
    const typing = context?.model.typing;
    return (
        <div className="axl-sc-foot">
            {context ? <LeadFormSlot model={context.model} ui={context.ui} autoFocus={context.autoFocus} /> : null}
            {typing ? (
                // Grows from zero height, so the thread above glides up instead of jumping by a row.
                <div className="axl-sc-grow"><div className="axl-sc-item axl-sc-typrow">
                    <div className="axl-sc-orow" style={{ marginTop: 8 }}>
                        <Avatar name={typing.name} photo={typing.avatarUrl} size={28} />
                        <div className="axl-sc-ob axl-sc-typ" aria-label="…"><i /><i /><i /></div>
                    </div>
                </div></div>
            ) : null}
        </div>
    );
}

function authorKey(author?: SiteChatAuthor | null) {
    return author ? `${author.name ?? ''}|${author.avatarUrl ?? ''}` : '';
}

type Receipt = 'sending' | 'delivered' | 'read';

/** A sent message is delivered once the server has it; read once the school's side read past it. */
function receiptOf(message: SiteChatMessage, readIndex: number): Receipt | null {
    if (message.local || message.status === 'held' || message.status === 'failed')
        return null;
    if (message.status === 'sending')
        return 'sending';
    return message.index <= readIndex ? 'read' : 'delivered';
}

const TICK = 'M4 12.5l4.5 4.5L18 7.5';
const TICK2 = 'M9 12.5l4.5 4.5L23 7.5';
const CLOCK = 'M12 7v5l3 2M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z';

function ReceiptMark({ receipt, ui }: { receipt: Receipt; ui: SiteChatUi }) {
    const title = receipt === 'read' ? ui.readMark : receipt === 'delivered' ? ui.delivered : undefined;
    return (
        <span className={`axl-sc-rcpt ${receipt}`} title={title} aria-label={title} data-sc={`receipt-${receipt}`}>
            <svg width={receipt === 'read' ? 18 : 14} height="14" viewBox={receipt === 'read' ? '0 0 26 24' : '0 0 24 24'} fill="none"
                stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                {receipt === 'sending' ? <path d={CLOCK} /> : <path d={TICK} />}
                {receipt === 'read' ? <path d={TICK2} /> : null}
            </svg>
        </span>
    );
}

function GuestBubble({ message, locale, ui, onResend, receipt }: {
    message: SiteChatMessage;
    locale: string;
    ui: SiteChatUi;
    onResend: (message: SiteChatMessage) => void;
    /** Shown only when the channel's widget has the marks on. */
    receipt?: Receipt | null;
}) {
    return (
        <div className={`axl-sc-g ${message.status === 'sending' || message.status === 'held' ? 'pending' : ''} ${hasBanner(message.attachments) ? 'media' : ''}`}>
            <FileView files={message.attachments} ui={ui} />
            {message.text ? <div className="axl-sc-tx">{linkify(message.text)}</div> : null}
            <div className="axl-sc-t">
                {message.createdDate ? timeLabel(message.createdDate, locale) : ''}
                {receipt ? <ReceiptMark receipt={receipt} ui={ui} /> : null}
            </div>
            {message.status === 'failed'
                ? <button type="button" className="axl-sc-failed" onClick={() => onResend(message)}>{ui.sendFailed}</button>
                : message.status === 'held'
                    ? <div className="axl-sc-held" data-sc="held">{ui.heldNote}</div>
                    : null}
        </div>
    );
}

function OperatorBubble({ message, author, avatar, locale, ui, onButton }: {
    message: SiteChatMessage;
    author: SiteChatAuthor | null;
    avatar: boolean;
    locale: string;
    ui: SiteChatUi;
    onButton: (button: NonNullable<SiteChatMessage['buttons']>[number]) => void;
}) {
    return (
        <div className="axl-sc-orow">
            {avatar ? <Avatar name={author?.name} photo={author?.avatarUrl} size={28} /> : <div className="axl-sc-sp" />}
            <div className="axl-sc-ocol">
                <div className={`axl-sc-ob ${hasBanner(message.attachments) ? 'media' : ''}`}>
                    <FileView files={message.attachments} ui={ui} />
                    {/* The AI assistant and scenarios write Markdown (`markdown`); everything else is plain text. */}
                    {message.text
                        ? <div className="axl-sc-tx">{message.markdown ? <MarkdownText text={message.text} linkify={linkify} safeUrl={safeUrl} /> : linkify(message.text)}</div>
                        : null}
                    {message.createdDate ? <div className="axl-sc-t">{timeLabel(message.createdDate, locale)}</div> : null}
                </div>
                {message.buttons?.length ? (
                    <div className="axl-sc-btns">
                        {message.buttons.map(button => button.type === 'default'
                            ? <button key={button.index} type="button" className="axl-sc-mb" onClick={() => onButton(button)}>{button.title}</button>
                            : <a key={button.index} className="axl-sc-mb" target="_blank" rel="noopener noreferrer"
                                href={button.type === 'phone' ? `tel:${button.link ?? ''}` : safeUrl(button.link ?? '')}>{button.title}</a>)}
                    </div>
                ) : null}
            </div>
        </div>
    );
}

/** A picture or a video is drawn as a banner across the bubble, and the bubble takes the banner's width. */
function hasBanner(files: SiteChatAttachment[] | null | undefined) {
    return !!files?.some(isBannerFile);
}

function FileView({ files, ui }: { files: SiteChatAttachment[] | null | undefined; ui: SiteChatUi }) {
    return files?.length ? <Attachments files={files} ui={ui} safeUrl={safeUrl} /> : null;
}

const SCREEN_ICON = 'M3 5h18v11H3zM8 20h8M12 16v4';

/**
 * The paperclip. Where the browser can capture the screen (desktop) and the channel accepts PNG, it opens a menu:
 * «File» or «Screenshot»; otherwise it opens the file picker straight away, as before.
 */
function AttachButton({ model, ui, hidden, onFiles }: { model: SiteChatModel; ui: SiteChatUi; hidden: boolean; onFiles: () => void }) {
    const [menu, setMenu] = useState(false);
    const ref = useRef<HTMLDivElement>(null);
    const allowed = model.config.allowedExtensions;
    const screenshot = model.canScreenshot && model.actions.screenshot && (allowed.length === 0 || allowed.includes('png'))
        ? model.actions.screenshot
        : null;

    useEffect(() => {
        if (!menu)
            return;
        // The widget lives in a Shadow DOM: composedPath() sees through it, event.target would not.
        const onDown = (e: PointerEvent) => {
            if (ref.current && !e.composedPath().includes(ref.current))
                setMenu(false);
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape')
                setMenu(false);
        };
        window.addEventListener('pointerdown', onDown, true);
        window.addEventListener('keydown', onKey, true);
        return () => {
            window.removeEventListener('pointerdown', onDown, true);
            window.removeEventListener('keydown', onKey, true);
        };
    }, [menu]);

    return (
        <div className={`axl-sc-attach ${hidden ? 'axl-sc-hid' : ''}`} ref={ref}>
            <button type="button" aria-label={ui.attach} aria-haspopup={screenshot ? 'menu' : undefined} aria-expanded={screenshot ? menu : undefined}
                onClick={() => screenshot ? setMenu(value => !value) : onFiles()}>
                <Icon d={PATH.clip} />
            </button>
            {menu && screenshot ? (
                <div className="axl-sc-amenu" role="menu" data-sc="attach-menu">
                    <button type="button" role="menuitem" onClick={() => { setMenu(false); onFiles(); }}>
                        <Icon d={PATH.clip} size={18} />{ui.attachFile}
                    </button>
                    <button type="button" role="menuitem" onClick={() => { setMenu(false); screenshot(); }}>
                        <Icon d={SCREEN_ICON} size={18} />{ui.screenshot}
                    </button>
                </div>
            ) : null}
        </div>
    );
}

/** The composer alone, for the gallery's V7 frame. */
export function ComposerView({ model }: { model: SiteChatModel }) {
    return <Composer model={model} ui={siteChatUi(model.config.lang)} />;
}

function Composer({ model, ui, autoFocus = false }: { model: SiteChatModel; ui: SiteChatUi; autoFocus?: boolean }) {
    const { composer, config } = model;
    const textRef = useRef<HTMLTextAreaElement>(null);
    // Mount only: the thread was just opened from the home card. preventScroll keeps the host page where it is.
    useEffect(() => {
        if (autoFocus)
            textRef.current?.focus({ preventScroll: true });
    }, []);
    const fileRef = useRef<HTMLInputElement>(null);
    const rowRef = useRef<HTMLDivElement>(null);
    const [voice, setVoice] = useState<SiteChatVoice | null>(null);
    const [recording, setRecording] = useState(false);
    const [notice, setNotice] = useState<string | null>(null);
    // The required form is under a waiting message: nothing new goes out until it is filled in.
    const locked = model.form.shown && model.form.required;
    const uploading = composer.uploads.some(item => item.state === 'uploading');
    const hasFiles = composer.uploads.some(item => item.state === 'ready');
    const canSend = !locked && (!!voice || (!uploading && (!!composer.text.trim() || hasFiles)));
    // Like the in-app chat: an empty composer offers the microphone where the send button goes.
    const showMic = !locked && config.voice && !voice && !composer.text.trim() && !hasFiles && !uploading;

    useEffect(() => {
        if (!notice)
            return;
        const timer = window.setTimeout(() => setNotice(null), 3000);
        return () => window.clearTimeout(timer);
    }, [notice]);

    const insertEmoji = (emoji: string) => {
        const area = textRef.current;
        const start = area?.selectionStart ?? composer.text.length;
        const end = area?.selectionEnd ?? composer.text.length;
        model.actions.setText(composer.text.slice(0, start) + emoji + composer.text.slice(end));
        window.requestAnimationFrame(() => {
            if (!area)
                return;
            area.focus();
            area.selectionStart = area.selectionEnd = start + emoji.length;
        });
    };

    const send = () => {
        if (voice) {
            model.actions.sendVoice(voice);
            setVoice(null);
            return;
        }
        model.actions.send();
    };

    useEffect(() => {
        const area = textRef.current;
        if (!area)
            return;
        area.style.height = 'auto';
        area.style.height = `${Math.min(area.scrollHeight, 22 * 4 + 14)}px`;
    }, [composer.text]);

    return (
        <div className={`axl-sc-cmpw ${locked ? 'locked' : ''}`} aria-disabled={locked || undefined}>
            {composer.uploads.length > 0 ? (
                <div className="axl-sc-ups" data-sc="uploads">
                    {composer.uploads.map(upload => <UploadItem key={upload.key} upload={upload} ui={ui} maxFileSizeMb={config.maxFileSizeMb}
                        onRemove={() => model.actions.removeUpload(upload.key)} onRetry={() => model.actions.retryUpload(upload.key)} />)}
                </div>
            ) : null}
            {composer.error || notice ? <div className="axl-sc-cerr">{notice ?? composer.error}</div> : null}
            <div className="axl-sc-cmp" ref={rowRef}>
                {voice ? <VoiceDraft voice={voice} ui={ui} onDelete={() => setVoice(null)} /> : null}
                <AttachButton model={model} ui={ui} hidden={!!voice || locked} onFiles={() => fileRef.current?.click()} />
                <input
                    ref={fileRef}
                    type="file"
                    accept={config.allowedExtensions.length ? config.allowedExtensions.map(ext => `.${ext}`).join(',') : undefined}
                    multiple
                    hidden
                    onChange={e => {
                        const files = Array.from(e.target.files ?? []);
                        e.target.value = '';
                        if (files.length)
                            model.actions.attach(files);
                    }}
                />
                <textarea
                    ref={textRef}
                    className={`axl-sc-inp ${voice ? 'axl-sc-hid' : ''}`}
                    rows={1}
                    placeholder={model.config.composerPlaceholder}
                    value={composer.text}
                    disabled={locked}
                    maxLength={config.maxLength}
                    onChange={e => model.actions.setText(e.target.value)}
                    onPaste={e => {
                        // A screenshot or a copied file lands as an attachment. Office apps put a picture of the copied
                        // cells next to their text; when there is text, the text is what the visitor meant to paste.
                        const files = Array.from(e.clipboardData?.files ?? []);
                        if (!files.length || e.clipboardData.getData('text/plain').trim())
                            return;
                        e.preventDefault();
                        model.actions.attach(files);
                    }}
                    onKeyDown={e => {
                        if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                            e.preventDefault();
                            if (canSend)
                                send();
                        }
                    }}
                />
                {config.emoji && !voice && !locked ? <EmojiButton ui={ui} onSelect={insertEmoji} disabled={recording} /> : null}
                {showMic || recording ? (
                    <VoiceRecorder
                        ui={ui}
                        areaRef={rowRef}
                        onRecorded={setVoice}
                        onRecordingChange={setRecording}
                        onError={setNotice}
                    />
                ) : (
                    <button type="button" className={`axl-sc-snd ${canSend ? '' : 'dis'}`} aria-label={ui.send} disabled={!canSend} onClick={send} data-sc="send">
                        <Icon d={PATH.send} size={18} />
                    </button>
                )}
            </div>
        </div>
    );
}

const URL_RE = /(https?:\/\/[^\s<>"']+|www\.[^\s<>"']+)/gi;

/** Plain text with clickable links. Never renders HTML from a message. */
export function linkify(text: string): React.ReactNode[] {
    const parts: React.ReactNode[] = [];
    let last = 0;
    text.replace(URL_RE, (match, _group, offset: number) => {
        if (offset > last)
            parts.push(text.slice(last, offset));
        const trimmed = match.replace(/[.,!?;:)]+$/, '');
        const href = trimmed.startsWith('http') ? trimmed : `https://${trimmed}`;
        parts.push(<a key={offset} href={href} target="_blank" rel="noopener noreferrer">{trimmed}</a>);
        last = offset + trimmed.length;
        return match;
    });
    if (last < text.length)
        parts.push(text.slice(last));
    return parts;
}

function safeUrl(url: string) {
    const value = (url ?? '').trim();
    if (/^(https?:|mailto:|tel:)/i.test(value))
        return value;
    if (/^[a-z]+:/i.test(value))
        return '#';
    return value ? `https://${value.replace(/^\/+/, '')}` : '#';
}

function dayKey(iso: string) {
    const date = new Date(iso);
    return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

function dayLabel(iso: string, locale: string, ui: SiteChatUi) {
    const date = new Date(iso);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);
    if (dayKey(iso) === dayKey(today.toISOString()))
        return ui.today;
    if (dayKey(iso) === dayKey(yesterday.toISOString()))
        return ui.yesterday;
    return new Intl.DateTimeFormat(locale, {
        day: 'numeric',
        month: 'long',
        ...(date.getFullYear() !== today.getFullYear() ? { year: 'numeric' } : {}),
    }).format(date);
}

function timeLabel(iso: string, locale: string) {
    return new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' }).format(new Date(iso));
}

export { formatSize } from './files';

function joinNames(names: string[], lang: SiteChatConfig['lang']) {
    if (names.length < 2)
        return names.join('');
    try {
        return new Intl.ListFormat(intlLocale(lang), { style: 'long', type: 'conjunction' }).format(names);
    } catch {
        return names.join(', ');
    }
}
